import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MapPin, X } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "12px",
};

interface LocationPickerProps {
  location: { lat: number; lng: number } | null;
  setLocation: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number } | null>
  >;
  setShowMap: React.Dispatch<React.SetStateAction<boolean>>;
  address: string;
  onLocationSelect: (
    lat: string,
    lng: string,
    addressDetails: AddressDetails,
  ) => void;
}

const defaultCenter = { lat: 26.8467, lng: 80.9462 };

export interface AddressDetails {
  fullAddress: string;
  city: string;
  state: string;
  pincode: string;
}

const LocationPicker = ({
  setLocation,
  setShowMap,
  address,
  onLocationSelect,
}: LocationPickerProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [addressDetails, setAddressDetails] = useState<AddressDetails>({
    fullAddress: "",
    city: "",
    state: "",
    pincode: "",
  });
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      process.env.GOOGLE_MAPS_API_KEY ||
      "",
  });

  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      setLoadingLocation(false);
      if (!currentLocation) setCurrentLocation(defaultCenter);
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLocation = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setCurrentLocation(newLocation);
        if (map) {
          map.panTo(newLocation);
          map.setZoom(16);
        }
        setLoadingLocation(false);
      },
      (error) => {
        setLoadingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error(
            "Location permission denied. Please select your location on the map.",
          );
        } else {
          toast.error("Unable to retrieve location. Default location selected.");
        }
        setCurrentLocation((prev) => prev || defaultCenter);
      },
      { timeout: 8000, maximumAge: 60000, enableHighAccuracy: false }
    );
  }, [map, currentLocation]);

  /** ✅ Fetch latitude/longitude from address using JS Geocoder */
  const fetchLatLngFromAddress = useCallback(
    (addr: string) => {
      if (!addr.trim()) {
        useCurrentLocation();
        return;
      }

      setLoadingLocation(true);
      if (window.google && window.google.maps && window.google.maps.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: addr }, (results, status) => {
          setLoadingLocation(false);
          if (status === "OK" && results?.[0]) {
            const loc = results[0].geometry.location;
            const newLocation = { lat: loc.lat(), lng: loc.lng() };
            setCurrentLocation(newLocation);
            if (map) {
              map.panTo(newLocation);
              map.setZoom(16);
            }
          } else {
            console.warn("Geocoding failed for address:", addr, status);
            useCurrentLocation();
          }
        });
      } else {
        useCurrentLocation();
      }
    },
    [map, useCurrentLocation],
  );

  /** ✅ Initialize location */
  useEffect(() => {
    if (!isLoaded) return;
    if (location && location.lat && location.lng) {
      setCurrentLocation(location);
    } else if (address && address.trim()) {
      fetchLatLngFromAddress(address);
    } else if (!currentLocation) {
      useCurrentLocation();
    }
  }, [isLoaded, address, location]);

  /** ✅ When map is clicked */
  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const newLocation = { lat, lng };
      setCurrentLocation(newLocation);
      if (map) map.panTo(newLocation);
    }
  };

  /** ✅ When marker is dragged */
  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setCurrentLocation({ lat, lng });
      if (map) map.panTo({ lat, lng });
    }
  };

  /** ✅ Confirm selected location */
  const handleConfirmLocation = () => {
    if (currentLocation) {
      onLocationSelect(
        currentLocation.lat.toString(),
        currentLocation.lng.toString(),
        addressDetails,
      );
      setLocation(currentLocation);
      setShowMap(false);
      toast.success("Location saved successfully!");
    } else {
      toast.error("Please select a location on the map");
    }
  };

  const reverseGeocode = (lat: number, lng: number) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat, lng } },
      (results: any, status: any) => {
        if (status === "OK" && results[0]) {
          const result = results[0];
          const components = result.address_components;

          let city = "";
          let state = "";
          let pincode = "";

          components.forEach((comp: any) => {
            if (comp.types.includes("locality")) city = comp.long_name;
            if (comp.types.includes("administrative_area_level_1"))
              state = comp.long_name;
            if (comp.types.includes("postal_code")) pincode = comp.long_name;
          });

          // ✅ Remove city/state/pincode from the main formatted address
          let cleanAddress = result.formatted_address;
          [city, state, pincode].forEach((part) => {
            if (part && cleanAddress.includes(part)) {
              cleanAddress = cleanAddress.replace(
                new RegExp(`,?\\s*${part}`, "i"),
                "",
              );
            }
          });

          // Trim trailing commas/spaces
          cleanAddress = cleanAddress.replace(/,\s*$/, "").trim();

          setAddressDetails({
            fullAddress: cleanAddress,
            city,
            state,
            pincode,
          });
        } else {
          setAddressDetails({
            fullAddress: "Unable to fetch address",
            city: "",
            state: "",
            pincode: "",
          });
        }
      },
    );
  };

  useEffect(() => {
    if (currentLocation) {
      reverseGeocode(currentLocation.lat, currentLocation.lng);
    }
  }, [currentLocation]);

  /** ✅ Map onLoad */
  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  return (
    <div className="p-6 rounded-xl border border-gray-200 shadow-lg bg-white w-3/4 h-3/4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-row gap-3">
          <h3 className="text-lg font-semibold">Select Location</h3>
          <button
            onClick={useCurrentLocation}
            className="px-2 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex flex-row gap-1 text-sm"
          >
            <MapPin size={16} /> Current Location
          </button>
        </div>

        <button
          onClick={() => setShowMap(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>

      {isLoaded ? (
        <div className="relative">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={currentLocation || defaultCenter}
            zoom={currentLocation ? 16 : 12}
            onClick={handleMapClick}
            onLoad={onLoad}
          >
            {currentLocation && (
              <Marker
                position={currentLocation}
                draggable // ✅ make marker draggable
                onDragEnd={handleMarkerDragEnd} // ✅ update location when dragged
              />
            )}
          </GoogleMap>

          {loadingLocation && (
            <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center text-gray-700 font-medium rounded-lg">
              <div className="animate-spin border-4 border-emerald-500 border-t-transparent rounded-full w-10 h-10 mb-3"></div>
              <p>Fetching your location...</p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
          <p>Loading map...</p>
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <div>
          {currentLocation && (
            <p className="text-sm text-gray-600">
              Selected:{" "}
              {addressDetails.fullAddress +
                "City : " +
                addressDetails.city +
                "State : " +
                addressDetails.state +
                "Pincode : " +
                addressDetails.pincode}
            </p>
          )}
        </div>
        <div className="space-x-2">
          <button
            onClick={() => setShowMap(false)}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmLocation}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            disabled={loadingLocation}
          >
            {loadingLocation ? "Detecting..." : "Confirm Location"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
